import type { SessionStore, SessionUser } from "../api/index.js";
import { ApiError, toRecoveryAction, type RecoveryAction, type ToRecoveryOptions } from "../recovery/index.js";
import type { AuthApi } from "./auth_api.js";
import type { IdentityProvider, IdentityUser } from "./identity.js";
import { canResend, normalizeOtp, otpFailure, otpIssued } from "./otp_step.js";
import type { SocialHandoff } from "./sign_in.js";
import { isValidEmail, isValidName, isValidPassword } from "./validation.js";

/**
 * Sign-up step machine (W-1). Parity with the app's SignUpCubit, plus the
 * W-1 security rules (every registerAndLogin carries the Firebase ID token):
 *
 *   form ── Firebase create ──▶ registerAndLogin ──▶ otp ── signupWithOtp ──▶ done
 *     │        └ email-already-in-use ─▶ checkEmail
 *     │              ├ existed ──────────────────────────▶ exists (→ Sign in)
 *     │              └ not existed (half-finished) ─▶ updatePassword ─▶ Firebase sign-in ─▶ registerAndLogin
 *     └─ social (Google/Apple handed over by sign-in): registerAndLogin ─▶ /login (cookie) ─▶ done
 *
 * Reload at the OTP step: the user doc exists unverified; sign-in's
 * "unverified → Resend" finishes it (no extra state kept here).
 */

export type SignUpStep = "form" | "otp" | "exists" | "done";
export type SignUpField = "firstName" | "familyName" | "email" | "password" | "terms" | "otp";

export interface SignUpForm {
  firstName: string;
  familyName: string;
  email: string;
  /** Ignored in social mode. */
  password: string;
  termsAccepted: boolean;
  marketingConsent: boolean;
}

export interface SignUpState {
  step: SignUpStep;
  busy: boolean;
  /** social: Google/Apple identity, email locked, no password. */
  mode: "email" | "social";
  email: string;
  firstName: string;
  familyName: string;
  providerId?: string;
  /** A checked partner code: shown locked and sent with sign-up. */
  partnerCode?: string;
  /** The partner link wasn't recognised: info banner, nothing sent. */
  partnerUnknown: boolean;
  otpId?: string;
  resendAvailableAt?: number;
  otpResetCount: number;
  error?: RecoveryAction;
  fieldErrors: Partial<Record<SignUpField, string>>;
  user?: SessionUser;
}

export interface SignUpDeps {
  identity: IdentityProvider;
  api: AuthApi;
  session: SessionStore;
  /** Tenant org (`TenantWebConfig.orgId`), never a literal. */
  orgId: string;
  partnerCode?: string;
  partnerUnknown?: boolean;
  /** A Google/Apple identity handed over by sign-in's notFound step. */
  social?: SocialHandoff;
  now?: () => number;
  recovery?: ToRecoveryOptions;
}

export interface SignUpFlow {
  getState(): SignUpState;
  subscribe(cb: () => void): () => void;
  validateField(field: Exclude<SignUpField, "otp" | "terms">, value: string): void;
  submit(form: SignUpForm): Promise<void>;
  submitOtp(code: string): Promise<void>;
  resendOtp(): Promise<void>;
  /** Leave the OTP / exists step for the form (input kept). */
  back(): void;
  dismissError(): void;
  /** Sign the Firebase identity out when sign-up is left unfinished. */
  abandon(): Promise<void>;
}

/** "Jane van der Berg" → first "Jane", family "van der Berg". */
export function splitDisplayName(name: string | undefined): { firstName: string; familyName: string } {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);
  return { firstName: parts[0] ?? "", familyName: parts.slice(1).join(" ") };
}

function firebaseCode(err: unknown): string | undefined {
  const code = (err as { code?: unknown } | null)?.code;
  return typeof code === "string" ? code : undefined;
}

function isAccountExists(err: unknown): boolean {
  return err instanceof ApiError && err.status === 409;
}

const MESSAGES: Record<Exclude<SignUpField, "otp">, string> = {
  firstName: "auth.validation.firstNameRequired",
  familyName: "auth.validation.familyNameRequired",
  email: "auth.validation.emailInvalid",
  password: "auth.validation.passwordTooShort",
  terms: "auth.validation.termsRequired",
};

export function createSignUpFlow(deps: SignUpDeps): SignUpFlow {
  const now = deps.now ?? Date.now;
  const social = deps.social;
  const names = splitDisplayName(social?.displayName);
  let state: SignUpState = {
    step: "form",
    busy: false,
    mode: social ? "social" : "email",
    email: social?.email ?? "",
    firstName: names.firstName,
    familyName: names.familyName,
    providerId: social?.providerId,
    partnerCode: deps.partnerCode,
    partnerUnknown: !deps.partnerCode && deps.partnerUnknown === true,
    otpResetCount: 0,
    fieldErrors: {},
  };
  const subs = new Set<() => void>();
  let finished = false;
  /**
   * The Firebase account this flow created (or resumed), so going back from
   * the OTP step or retrying a failed registerAndLogin signs back in to it
   * instead of hitting email-already-in-use → "exists".
   */
  let ours: { email: string; registered: boolean } | null = null;

  const set = (patch: Partial<SignUpState>) => {
    state = { ...state, ...patch };
    for (const s of subs) s();
  };
  const fail = (err: unknown) => set({ busy: false, error: toRecoveryAction(err, deps.recovery) });
  const guardBusy = () => {
    if (state.busy) return true;
    set({ busy: true, error: undefined });
    return false;
  };
  const finish = (token: string, user: SessionUser) => {
    finished = true;
    deps.session.setSession(token, user);
    set({ busy: false, step: "done", user, error: undefined, fieldErrors: {} });
  };
  const toExists = async () => {
    // Someone else's (or an already-finished) account: nothing to continue.
    await deps.identity.signOut().catch(() => undefined);
    set({ busy: false, step: "exists" });
  };

  const validate = (field: Exclude<SignUpField, "otp">, value: string | boolean): string | undefined => {
    switch (field) {
      case "firstName":
      case "familyName":
        return isValidName(String(value)) ? undefined : MESSAGES[field];
      case "email":
        return isValidEmail(String(value)) ? undefined : MESSAGES.email;
      case "password":
        return isValidPassword(String(value)) ? undefined : MESSAGES.password;
      case "terms":
        return value === true ? undefined : MESSAGES.terms;
    }
  };

  /** registerAndLogin, then either the OTP step or (verified) the cookie. */
  const register = async (user: IdentityUser, form: SignUpForm, email: string) => {
    const idToken = await user.getIdToken();
    const res = await deps.api.registerAndLogin({
      id: user.uid,
      name: form.firstName.trim(),
      familyName: form.familyName.trim(),
      email,
      orgId: deps.orgId,
      firebaseIdToken: idToken,
      ...(state.mode === "social" ? { emailVerified: true } : {}),
      marketingConsent: form.marketingConsent,
      ...(state.partnerCode ? { partnerCode: state.partnerCode } : {}),
    });
    if (res.otpId && !res.token) {
      if (ours?.email === email) ours.registered = true;
      set({ busy: false, step: "otp", ...otpIssued(res.otpId, now()) });
      return;
    }
    // Verified identity: W-0's /login sets the refresh cookie.
    const login = await deps.api.login(idToken);
    finish(login.token, login.user);
  };

  /** Email mode: Firebase account (new, or a half-finished one) → identity. */
  const emailIdentity = async (email: string, password: string): Promise<IdentityUser | "exists" | null> => {
    try {
      const created = await deps.identity.createUserWithPassword(email, password);
      ours = { email, registered: false };
      return created;
    } catch (err) {
      if (firebaseCode(err) !== "auth/email-already-in-use") {
        fail(err);
        return null;
      }
    }
    const { existed } = await deps.api.checkEmail(email);
    if (existed) return "exists";
    // Half-finished sign-up (Firebase account, no KoverNow user): mobile's
    // updatePassword recovery, then sign in with the new password.
    const { updated } = await deps.api.updatePassword(email, password);
    if (!updated) {
      set({
        busy: false,
        error: {
          errorCode: "signup_cannot_resume",
          messageKey: "errors.signup_cannot_resume",
          action: "sign_in",
          kind: "recoverable",
        },
      });
      return null;
    }
    const resumed = await deps.identity.signInWithPassword(email, password);
    ours = { email, registered: false };
    return resumed;
  };

  return {
    getState: () => state,
    subscribe(cb) {
      subs.add(cb);
      return () => {
        subs.delete(cb);
      };
    },

    validateField(field, value) {
      if (field === "email" && state.mode === "social") return;
      set({ fieldErrors: { ...state.fieldErrors, [field]: validate(field, field === "email" ? value.trim() : value) } });
    },

    async submit(form) {
      const email = state.mode === "social" ? state.email : form.email.trim().toLowerCase();
      const fieldErrors: SignUpState["fieldErrors"] = {
        firstName: validate("firstName", form.firstName),
        familyName: validate("familyName", form.familyName),
        // The provider's email is locked: never a field error the user can't fix.
        email: state.mode === "social" ? undefined : validate("email", email),
        password: state.mode === "social" ? undefined : validate("password", form.password),
        terms: validate("terms", form.termsAccepted),
      };
      const firstName = form.firstName;
      const familyName = form.familyName;
      if (Object.values(fieldErrors).some(Boolean)) {
        set({ fieldErrors, email, firstName, familyName });
        return;
      }
      if (guardBusy()) return;
      set({ email, firstName, familyName, fieldErrors: {} });
      try {
        let user: IdentityUser | "exists" | null;
        if (state.mode === "social") {
          const idToken = await deps.identity.getIdToken();
          if (!idToken || !social) {
            // The in-memory Google/Apple session is gone (e.g. a reload).
            set({
              busy: false,
              error: {
                errorCode: "auth_social_expired",
                messageKey: "errors.auth_social_expired",
                action: "reauthenticate",
                kind: "recoverable",
              },
            });
            return;
          }
          user = { uid: social.uid, email, emailVerified: true, getIdToken: async () => idToken };
        } else if (ours?.email === email) {
          const registered = ours.registered;
          user = await deps.identity.signInWithPassword(email, form.password);
          if (registered) {
            // The unverified user doc already exists (name edits aren't
            // re-sent): just send a fresh verify code.
            const { otpId } = await deps.api.sendVerificationOtpEmail(email);
            set({ busy: false, step: "otp", ...otpIssued(otpId, now()), otpResetCount: state.otpResetCount + 1 });
            return;
          }
        } else {
          user = await emailIdentity(email, form.password);
        }
        if (user === null) return;
        if (user === "exists") {
          await toExists();
          return;
        }
        await register(user, form, email);
      } catch (err) {
        if (isAccountExists(err)) {
          await toExists();
          return;
        }
        fail(err);
      }
    },

    async submitOtp(code) {
      const otp = normalizeOtp(code);
      if (!otp) {
        set({ fieldErrors: { otp: "auth.validation.otpLength" } });
        return;
      }
      if (!state.otpId || guardBusy()) return;
      set({ fieldErrors: {} });
      try {
        // signupWithOtp verifies the email, provisions usersPrivate and sets the cookie.
        const res = await deps.api.signupWithOtp(state.otpId, otp);
        finish(res.token, res.user);
      } catch (err) {
        set(otpFailure(err, state, deps.recovery));
      }
    },

    async resendOtp() {
      const dead = state.error?.action === "resend_code";
      if (!state.otpId || (!dead && !canResend(state, now())) || guardBusy()) return;
      try {
        const { otpId } = await deps.api.resendOtp(state.otpId);
        set({ busy: false, ...otpIssued(otpId, now()), otpResetCount: state.otpResetCount + 1 });
      } catch (err) {
        fail(err);
      }
    },

    back() {
      set({ step: "form", busy: false, error: undefined, fieldErrors: {}, otpId: undefined, resendAvailableAt: undefined });
    },

    dismissError() {
      set({ error: undefined });
    },

    async abandon() {
      if (finished) return;
      await deps.identity.signOut().catch(() => undefined);
    },
  };
}
